/**
 * RevenueCat bridge (Capacitor).
 *
 * Encapsulates SDK init, user identification, offerings fetch, purchase,
 * restore, and backend sync. Gracefully degrades on web — every function
 * is a no-op (or throws a clean error) when Capacitor.isNativePlatform()
 * is false, so the rest of the codebase doesn't have to platform-check.
 *
 * Configuration via build-time env vars (CRA REACT_APP_* prefix):
 *   - REACT_APP_REVENUECAT_IOS_KEY      — RC public SDK key for iOS app
 *   - REACT_APP_REVENUECAT_ANDROID_KEY  — RC public SDK key for Android app
 *
 * Entitlement names (must match the RevenueCat dashboard entitlements):
 *   `pro`     → 3-job tier ($59.99/mo)
 *   `premium` → unlimited tier ($79.99/mo)
 *
 * Package identifiers in the default offering:
 *   `pro_monthly`
 *   `premium_monthly`
 */
import { isNative, platform } from "@/lib/native";
import { api } from "@/lib/api";

const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

// Tier ID → (entitlement name, package identifier on RC).
const TIER_CONFIG = {
  pro: { entitlement: "pro", packageId: "pro_monthly" },
  premium: { entitlement: "premium", packageId: "premium_monthly" },
};

let _initialized = false;

function getKey() {
  if (platform() === "ios") return IOS_KEY;
  if (platform() === "android") return ANDROID_KEY;
  return "";
}

export function isIapAvailable() {
  return isNative() && !!getKey();
}

/** Initialize the RevenueCat SDK. Idempotent. */
export async function initIap() {
  if (!isNative()) return;
  const key = getKey();
  if (!key) {
    console.info("RevenueCat: SDK key not configured for", platform(), "— IAP disabled.");
    return;
  }
  if (_initialized) return;
  try {
    const { Purchases, LOG_LEVEL } = await import("@revenuecat/purchases-capacitor");
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({ apiKey: key });
    _initialized = true;
  } catch (e) {
    console.error("RevenueCat init failed:", e);
  }
}

export async function identifyIap(userId) {
  if (!isIapAvailable() || !userId) return;
  if (!_initialized) await initIap();
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.logIn({ appUserID: userId });
  } catch (e) {
    console.warn("RevenueCat logIn failed:", e);
  }
}

export async function logOutIap() {
  if (!isIapAvailable() || !_initialized) return;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.logOut();
  } catch (e) {
    console.debug("RevenueCat logOut:", e?.message || e);
  }
}

/** Fetch the package for a specific tier ('pro' | 'premium'). */
export async function getTierPackage(tier) {
  if (!isIapAvailable()) return null;
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return null;
  if (!_initialized) await initIap();
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    const current = offerings?.current;
    if (!current) return null;
    const pkgs = current.availablePackages || [];
    return (
      pkgs.find((p) => p.identifier === cfg.packageId)
      || pkgs.find((p) => p.identifier?.toLowerCase().includes(tier))
      || null
    );
  } catch (e) {
    console.error("RevenueCat getOfferings failed:", e);
    return null;
  }
}

/** Back-compat alias for the old single-tier helper. */
export const getPremiumPackage = () => getTierPackage("premium");

/** Execute a purchase for the specified tier. */
export async function purchaseTier(tier) {
  if (!isIapAvailable()) {
    return { ok: false, error: "IAP not available on this platform." };
  }
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return { ok: false, error: `Unknown tier: ${tier}` };
  const pkg = await getTierPackage(tier);
  if (!pkg) return { ok: false, error: `No package available for ${tier}.` };
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    const customerInfo = result?.customerInfo;
    await syncIapToBackend(customerInfo);
    return {
      ok: true,
      customerInfo,
      tier,
      premiumUntil:
        customerInfo?.entitlements?.active?.[cfg.entitlement]?.expirationDate || null,
    };
  } catch (e) {
    if (e?.userCancelled) return { ok: false, userCancelled: true };
    console.error("Purchase failed:", e);
    return { ok: false, error: e?.message || "Purchase failed." };
  }
}

/** Back-compat alias — purchases Premium tier specifically. */
export const purchasePremium = () => purchaseTier("premium");

/** Restore previous purchases (e.g., after reinstall). */
export async function restorePurchases() {
  if (!isIapAvailable()) {
    return { ok: false, error: "IAP not available on this platform." };
  }
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const { customerInfo } = await Purchases.restorePurchases();
    const active = !!(
      customerInfo?.entitlements?.active?.premium
      || customerInfo?.entitlements?.active?.pro
    );
    if (active) await syncIapToBackend(customerInfo);
    return { ok: true, restored: active, customerInfo };
  } catch (e) {
    console.error("Restore failed:", e);
    return { ok: false, error: e?.message || "Restore failed." };
  }
}

async function syncIapToBackend(customerInfo) {
  if (!customerInfo) return;
  try {
    await api.post("/billing/iap/sync", { customer_info: customerInfo });
  } catch (e) {
    console.warn("Backend IAP sync failed (webhook will fix it):", e?.message || e);
  }
}
